/**
 * Field Name Transformation Utility
 * Converts between snake_case (backend) and camelCase (frontend) conventions
 *
 * This fixes the mismatch where backend sends parent_id, start_date, end_date
 * but frontend expects parentId, startDate, endDate
 *
 * Note: This module intentionally uses `any` for dynamic backend/frontend data
 * transformation where the shape is not statically known.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { ProjectNode } from '@/shared/types/projectData';

/**
 * Convert snake_case to camelCase
 */
function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

/**
 * Convert camelCase to snake_case
 */
function camelToSnake(str: string): string {
  return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
}

/**
 * Transform a single node from backend format to frontend format
 * Converts snake_case fields to camelCase
 */
export function transformNodeFromBackend(node: any): ProjectNode {
  if (!node) return node;
  
  // Create a new object with transformed fields
  const transformed: any = {};
  
  // Handle all fields - automatic transformation via snakeToCamel
  for (const key in node) {
    const value = node[key];
    const camelKey = snakeToCamel(key);

    // Special handling for nested objects
    if (key === 'view_state' && value && typeof value === 'object') {
      transformed.viewState = transformObjectFromBackend(value);
    } else if (key === 'custom_fields' && value && typeof value === 'object') {
      transformed.customFields = value;
    } else if (key === 'authority_entries' && Array.isArray(value)) {
      transformed.authorityEntries = value.map((entry: any) => transformObjectFromBackend(entry));
    } else if (key === 'linked_docs' && Array.isArray(value)) {
      transformed.linkedDocs = value.map((doc: any) => transformObjectFromBackend(doc));
    } else {
      transformed[camelKey] = value;
    }
  }

  // Special handling for milestone nodes
  // Milestones use 'date' field in the frontend, but backend sends 'due_date'
  if (transformed.type === 'milestone' && transformed.dueDate && !transformed.date) {
    transformed.date = transformed.dueDate;
  }
  
  // Special handling for task nodes
  // Tasks use 'dueDate' as their end date for timeline and dependency rendering
  // Backend sends tasks with start_date and due_date, but frontend Gantt components
  // expect startDate and endDate to render timeline bars and dependency lines
  if (transformed.type === 'task' && transformed.dueDate && !transformed.endDate) {
    transformed.endDate = transformed.dueDate;
  }

  // Checklists are owned by task and group nodes. Strip stale checklist data
  // from milestones/documents/reports so unsupported payloads do not render.
  if (transformed.type === 'task' || transformed.type === 'group') {
    if (!Array.isArray(transformed.checklist) && Array.isArray(transformed.checklists)) {
      transformed.checklist = transformed.checklists;
    }
    if (Array.isArray(transformed.checklist) && !Array.isArray(transformed.checklists)) {
      transformed.checklists = transformed.checklist;
    }
  } else {
    delete transformed.checklist;
    delete transformed.checklists;
  }
  
  return transformed as ProjectNode;
}

function looksLikeBackendNode(node: any): boolean {
  if (!node || typeof node !== 'object' || Array.isArray(node)) return false;

  return (
    'parent_id' in node ||
    'start_date' in node ||
    'end_date' in node ||
    'due_date' in node ||
    'view_state' in node ||
    'custom_fields' in node ||
    'linked_docs' in node ||
    'development_roles' in node ||
    'visibility_tier' in node ||
    'visible_to_account_ids' in node ||
    'classification_source' in node ||
    'classification_confidence' in node ||
    'authority_owner_id' in node ||
    'authority_entries' in node ||
    'checklists' in node
  );
}

/**
 * Transform a generic object from backend format to frontend format
 */
function transformObjectFromBackend(obj: any): any {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(transformObjectFromBackend);
  
  const transformed: any = {};
  for (const key in obj) {
    const camelKey = snakeToCamel(key);
    transformed[camelKey] = transformObjectFromBackend(obj[key]);
  }
  return transformed;
}

/**
 * Transform project data from backend format to frontend format
 */
export function transformProjectDataFromBackend(data: any): any {
  if (!data) return data;
  
  const transformed = { ...data };
  
  // Transform nodes array
  if (data.nodes && Array.isArray(data.nodes)) {
    transformed.nodes = data.nodes.map(transformNodeFromBackend);
  }
  
  // Transform project metadata fields
  if ('project_id' in data) {
    transformed.projectId = data.project_id;
  }
  if ('project_name' in data) {
    transformed.projectName = data.project_name;
  }
  if ('company_name' in data) {
    transformed.companyName = data.company_name;
  }
  if ('project_slug' in data) {
    transformed.projectSlug = data.project_slug;
  }
  if ('created_at' in data) {
    transformed.createdAt = data.created_at;
  }
  if ('updated_at' in data) {
    transformed.updatedAt = data.updated_at;
  }
  if ('node_count' in data) {
    transformed.nodeCount = data.node_count;
  }
  if (Array.isArray(data.root_node_ids)) {
    transformed.rootNodeIds = [...data.root_node_ids];
  }
  
  // Transform settings if present
  if (data.settings && typeof data.settings === 'object') {
    const transformedSettings = transformObjectFromBackend(data.settings);
    transformed.settings = transformedSettings;
    transformed.metadata = {
      ...(data.metadata && typeof data.metadata === 'object' ? transformObjectFromBackend(data.metadata) : {}),
      settings: {
        ...(data.metadata?.settings && typeof data.metadata.settings === 'object'
          ? transformObjectFromBackend(data.metadata.settings)
          : {}),
        ...transformedSettings,
      },
    };
  }

  if (Array.isArray(data.kanban_columns)) {
    transformed.kanbanColumns = transformObjectFromBackend(data.kanban_columns);
  } else if (Array.isArray(transformed.settings?.kanbanColumns)) {
    transformed.kanbanColumns = transformed.settings.kanbanColumns;
  }
  
  return transformed;
}

export function transformRealtimeOperationFromBackend(operation: any): any {
  if (!operation || typeof operation !== 'object') return operation;

  const transformed = { ...operation };

  // The canonical backend envelope always includes optional operation IDs,
  // including `nodeId: null` for project-level operations such as Kanban
  // column reorders. The queue validator correctly rejects null where the
  // wire contract otherwise requires a string; absence is the equivalent
  // valid representation. Do not alter nullable parent-move semantics.
  for (const key of ['nodeId', 'parentId', 'columnId', 'sessionId', 'userId']) {
    if (transformed[key] === null) {
      delete transformed[key];
    }
  }

  if (looksLikeBackendNode(transformed.data)) {
    transformed.data = transformNodeFromBackend(transformed.data);
  }

  if (Array.isArray(transformed.updatedNodesFull)) {
    transformed.updatedNodesFull = transformed.updatedNodesFull.map((node: any) =>
      looksLikeBackendNode(node) ? transformNodeFromBackend(node) : node
    );
  }

  if (Array.isArray(transformed.updatedNodes)) {
    transformed.updatedNodes = transformed.updatedNodes.map((entry: any) => {
      if (!entry || typeof entry !== 'object' || !looksLikeBackendNode(entry.node)) {
        return entry;
      }
      return {
        ...entry,
        node: transformNodeFromBackend(entry.node),
      };
    });
  }

  if (Array.isArray(transformed.updates)) {
    transformed.updates = transformed.updates.map((entry: any) => {
      if (!entry || typeof entry !== 'object' || !looksLikeBackendNode(entry.data)) {
        return entry;
      }
      return {
        ...entry,
        data: transformNodeFromBackend(entry.data),
      };
    });
  }

  return transformed;
}

/**
 * Transform project info from backend format to frontend format
 */
export function transformProjectInfoFromBackend(project: any): any {
  if (!project) return project;
  
  const transformed: any = {};
  
  // Automatic transformation via snakeToCamel
  for (const key in project) {
    const value = project[key];
    const camelKey = snakeToCamel(key);
    transformed[camelKey] = value;
  }

  return transformed;
}

/**
 * Transform a node from frontend format to backend format
 * Converts camelCase fields to snake_case for API calls
 */
export function transformNodeToBackend(node: any): any {
  if (!node) return node;
  
  const transformed: any = {};
  
  // Automatic transformation via camelToSnake
  for (const key in node) {
    const value = node[key];
    const snakeKey = camelToSnake(key);

    // Special handling for nested objects
    if (key === 'viewState' && value && typeof value === 'object') {
      transformed.view_state = transformObjectToBackend(value);
    } else if (key === 'customFields' && value && typeof value === 'object') {
      transformed.custom_fields = value;
    } else if (key === 'authorityEntries' && Array.isArray(value)) {
      transformed.authority_entries = value.map((entry: any) => transformObjectToBackend(entry));
    } else if (key === 'linkedDocs' && Array.isArray(value)) {
      transformed.linked_docs = value.map((doc: any) => transformObjectToBackend(doc));
    } else {
      transformed[snakeKey] = value;
    }
  }

  // Special handling for milestone nodes
  // Frontend uses 'date' field, but backend expects 'due_date'
  if (node.type === 'milestone' && node.date && !('due_date' in transformed)) {
    transformed.due_date = node.date;
  }
  
  return transformed;
}

/**
 * Transform a generic object from frontend format to backend format
 */
function transformObjectToBackend(obj: any): any {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(transformObjectToBackend);
  
  const transformed: any = {};
  for (const key in obj) {
    const snakeKey = camelToSnake(key);
    transformed[snakeKey] = transformObjectToBackend(obj[key]);
  }
  return transformed;
}

/**
 * Transform operation data for backend API calls
 */
export function transformOperationToBackend(operation: any): any {
  if (!operation) return operation;
  
  const transformed = { ...operation };
  
  // Transform node data if present
  if (operation.data) {
    transformed.data = transformNodeToBackend(operation.data);
  }
  
  // Transform updates array for batch operations (ONE WAY: use data field)
  if (operation.updates && Array.isArray(operation.updates)) {
    transformed.updates = operation.updates.map((update: any) => ({
      ...update,
      data: transformNodeToBackend(update.data)
    }));
  }
  
  // Transform nodeId to node_id
  if ('nodeId' in operation) {
    transformed.node_id = operation.nodeId;
  }
  if ('parentId' in operation) {
    transformed.parent_id = operation.parentId;
  }
  
  return transformed;
}
